import { MacroNutrientData } from '../../models/macro-nutrient-data';

/**
 * Windows and figures for the macro chart, where each bar is one element's applications for the
 * whole season and the part applied in the picked period is shown darker at the bottom.
 *
 * A season runs 01/06 to 31/05, the rule the dashboard's season dropdown uses. Dates are local
 * calendar dates written as yyyy-MM-dd, the form the sync service takes.
 */

export interface DateWindow {
  from: string;
  to: string;
}

export interface Season extends DateWindow {
  /** As the season dropdown writes it, e.g. "2026/2027". */
  label: string;
}

export interface ChartWindows {
  season: Season;
  /** The picked dates clipped to the season; null when they do not overlap it. */
  period: DateWindow | null;
}

/** What the dashboard publishes on fieldData$ that the windows depend on. */
export interface DashboardDates {
  startDate?: Date | null;
  endDate?: Date | null;
  userChangedDates?: boolean;
  season?: string | null;
}

export type MacroElementKey = 'n' | 'p' | 'k' | 'ca' | 'mg' | 's';

export interface SeasonSplit {
  /** Applied in the period, per season cultivar. */
  period: number[];
  /** Applied in the rest of the season, never below 0. */
  rest: number[];
  /** Applied in the whole season, per season cultivar. */
  total: number[];
}

/*
 * The FMS endpoints count both the start and the end day: fert `application_date >= :fromDate AND
 * < :toDate + 1`, spray `spray_date BETWEEN :fromDate AND :toDate` (sync-service FmsFarmRepository
 * getFertByBlock and getSprayByBlockView, inclusive since 27/10/2026; before that both end days were
 * left out). So the subtitle names the picked days as they are. The web app and that sync-service
 * change have to be deployed together.
 */

export function isoDate(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mm}-${dd}`;
}

/** A season's label: 2026 gives "2026-2027". Dates and seasons use dashes (Heinrich, 28/10/2026). */
export function seasonLabel(startYear: number): string {
  return `${startYear}-${startYear + 1}`;
}

function seasonStarting(startYear: number): Season {
  return { label: seasonLabel(startYear), from: `${startYear}-06-01`, to: `${startYear + 1}-05-31` };
}

/** The season a local date falls in: 01/06 starts a new one. */
export function seasonOf(date: Date): Season {
  return seasonStarting(date.getMonth() >= 5 ? date.getFullYear() : date.getFullYear() - 1);
}

/**
 * "2026-2027" gives the 2026-06-01 to 2027-05-31 season; anything else gives null. The old "2026/2027" is
 * still read, so a label kept from before the change cannot leave a chart without dates.
 */
export function seasonFromLabel(label: string | null | undefined): Season | null {
  const match = /^(\d{4})[-/](\d{4})$/.exec((label ?? '').trim());
  if (!match || Number(match[2]) !== Number(match[1]) + 1) return null;
  return seasonStarting(Number(match[1]));
}

/**
 * After a date pick the season is the one holding the picked end date. Without a pick the season
 * dropdown chooses it, as it did before. The dropdown value is not used after a pick because the
 * date change sets it from the calendar year (field-dashboard-view.component.ts onDateChange).
 * The period is the picked range clipped to the season, so it is always part of the season.
 */
export function chartWindows(data: DashboardDates): ChartWindows | null {
  if (!data.startDate || !data.endDate) return null;
  const season = data.userChangedDates
    ? seasonOf(data.endDate)
    : seasonFromLabel(data.season) ?? seasonOf(data.endDate);
  const from = maxIso(isoDate(data.startDate), season.from);
  const to = minIso(isoDate(data.endDate), season.to);
  return { season, period: from <= to ? { from, to } : null };
}

export function sameWindows(a: ChartWindows | null, b: ChartWindows | null): boolean {
  if (!a || !b) return a === b;
  return a.season.from === b.season.from && a.season.to === b.season.to
    && (a.period?.from ?? null) === (b.period?.from ?? null)
    && (a.period?.to ?? null) === (b.period?.to ?? null);
}

/**
 * One element's season split by period. The two responses each list their own cultivars, so the
 * period is matched to the season by cultivar name, not by position. A null list (an unmapped
 * field returns an empty response) counts as nothing applied.
 */
export function splitBySeason(season: MacroNutrientData | null, period: MacroNutrientData | null,
                              key: MacroElementKey): SeasonSplit {
  const cultivars = season?.cultivars ?? [];
  const seasonValues = season?.[key] ?? [];
  const periodByCultivar = new Map<string, number>();
  (period?.cultivars ?? []).forEach((cultivar, i) => periodByCultivar.set(cultivar, (period?.[key] ?? [])[i] ?? 0));

  const split: SeasonSplit = { period: [], rest: [], total: [] };
  cultivars.forEach((cultivar, i) => {
    const total = seasonValues[i] ?? 0;
    const inPeriod = periodByCultivar.get(cultivar) ?? 0;
    split.total.push(total);
    split.period.push(inPeriod);
    split.rest.push(Math.max(0, round2(total - inPeriod)));
  });
  return split;
}

/** The colour mixed with white; amount 0 keeps it, 1 gives white. */
export function tint(hex: string, amount: number = 0.5): string {
  const channel = (start: number) => {
    const value = parseInt(hex.slice(start, start + 2), 16);
    return Math.round(value + (255 - value) * amount).toString(16).padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/** 2 decimals, decimal comma: 15 gives "15,00". */
export function amountLabel(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

/** The chart's subtitle: which days the dark and light parts hold, first and last day included. */
export function windowsSubtext(windows: ChartWindows): string {
  const seasonDays = `(${windows.season.from} to ${windows.season.to})`;
  if (!windows.period) {
    return `Dark: none, the picked dates are outside season ${windows.season.label}\n`
      + `Light: season ${windows.season.label} ${seasonDays}`;
  }
  return `Dark: ${windows.period.from} to ${windows.period.to}\n`
    + `Light: rest of season ${windows.season.label} ${seasonDays}`;
}

/** For text placed in tooltip HTML: cultivar names come from the FMS. */
export function escapeHtml(text: string): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function maxIso(a: string, b: string): string {
  return a > b ? a : b;
}

function minIso(a: string, b: string): string {
  return a < b ? a : b;
}
